import { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Grid,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Button } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import api from '../config/api';
import useStore from "../config/store";
import { useTranslation } from 'react-i18next';

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 0,
  p: 4,
  border: "none",
  outline: "none",
};

// Política unificada con el backend: mínimo 6 caracteres y al menos un número
const isPasswordValid = (password) => password.length >= 6 && /\d/.test(password);

const UserPasswordModal = ({ open, onClose, user }) => {
  const [password, setPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { authToken, showAlert, user: loggedUser } = useStore();
  const { t } = useTranslation();

  // Cambiar la PROPIA contraseña exige acreditar la actual; el flujo de un
  // admin cambiando la de otro usuario se mantiene sin ese requisito.
  const isSelf = !!(
    (loggedUser?.uid && user?.uid && loggedUser.uid === user.uid) ||
    (loggedUser?.email && user?.email && loggedUser.email === user.email)
  );

  useEffect(() => {
    if (open) {
      setPassword("");
      setCurrentPassword("");
      setShowPassword(false);
      setShowCurrentPassword(false);
    }
  }, [open]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put(
        `/users/modifyPassword/${user.uid}`,
        isSelf ? { password, currentPassword } : { password },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      showAlert(t('users.userPasswordSuccess'), "success");
      setPassword("");
      setCurrentPassword("");
      onClose();
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      if (code === 'WRONG_CURRENT_PASSWORD') {
        showAlert(t('users.wrongCurrentPassword'), "error");
      } else if (code === 'CURRENT_PASSWORD_REQUIRED') {
        showAlert(t('users.currentPasswordRequired'), "error");
      } else {
        showAlert(t('users.userPasswordError'), "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const eyeAdornment = (show, setShow) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton onClick={() => setShow((prev) => !prev)} edge="end">
          {show ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  });

  const passwordOk = isPasswordValid(password);
  const canSave = passwordOk && (!isSelf || currentPassword.length > 0);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" sx={{ textAlign: "center", mb: 3 }}>
          {t('users.userPasswordTitle')} {user?.name}
        </Typography>

        {isSelf && (
          <TextField
            fullWidth
            label={t('users.currentPassword')}
            type={showCurrentPassword ? "text" : "password"}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={eyeAdornment(showCurrentPassword, setShowCurrentPassword)}
          />
        )}

        <TextField
          fullWidth
          label={t('users.newPassword')}
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={password.length > 0 && !passwordOk}
          helperText={t('users.passwordPolicy')}
          InputProps={eyeAdornment(showPassword, setShowPassword)}
        />

        <Grid container spacing={2} justifyContent="center" sx={{ mt: 4 }}>
          <Grid item>
            <Button onClick={onClose} variant="outlined" disabled={loading}>
              {t('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <LoadingButton
              variant="contained"
              onClick={handleSave}
              loading={loading}
              disabled={!canSave}
            >
              {t('save')}
            </LoadingButton>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default UserPasswordModal;
